import { AppBar, Box, CssBaseline, Drawer, styled, Typography, useMediaQuery, useTheme } from '@mui/material'
import { useEffect, useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { To, useLocation, useNavigate } from 'react-router-dom'
import { AppRoutesNavigation } from 'src/app-routes'
import Footer from 'src/containers/footer/Footer'
import { NavigationContainer } from 'src/containers/TopBar/Index.style'
import { SidebarCtx } from 'src/context/SidebarContext'
import { MUIThemeColors } from 'src/mui-theme'
import { mainTopBarItems } from 'src/screens/main-top-bar-items'
import { closeAllDialog } from 'src/show-dialog'
import { drawerWidth } from 'src/styles/constants'
import { Flex } from 'src/styles/flexComponent'
import { padding } from 'src/styles/paddingCss'
import { getThemeColor } from 'src/utils'
import { TopBar } from '../containers/TopBar/Index'
import { DrawerLayout } from './SidebarLayout'
import { useFeatureFlagsCtx, useUserCtx } from '../hooks/context-hooks'
import { hasMainNavAccess } from 'src/utils/appFeatureAccess'
import { useViewportHeight } from '../hooks/useViewportHeight'

const Index = ({ children }) => {
  const [collapsibleSidebarOpen, setCollapsibleSidebarOpen] = useState(true)
  const theme = useTheme()
  const { pathname } = useLocation()
  const { user } = useUserCtx()
  const isMedium = useMediaQuery<any>(theme.breakpoints.down('md'))
  const isSmall = useMediaQuery<any>(theme.breakpoints.down('sm'))
  const firstSegment = pathname.split('/')[1]
  const isCertificateDatabasePage = pathname.includes('/certificate-database')
  const isISdebarHidden =
    ['locality', 'service-station', 'storage', 'project', 'profile'].includes(firstSegment) || isCertificateDatabasePage
  const viewportHeight = useViewportHeight()

  useEffect(() => {
    closeAllDialog()
    if ('FreshworksWidget' in window) {
      // @ts-ignore
      window.FreshworksWidget('prefill', 'ticketForm', {
        custom_fields: {
          cf_url: location.pathname,
        },
      })
    }
  }, [location])
  useEffect(() => {
    const script = document.createElement('script')
    const reference = document.createElement('script')

    script.innerHTML = `
	window.fwSettings={
	'widget_id':80000006881
	};
	!function(){if("function"!=typeof window.FreshworksWidget){var n=function(){n.q.push(arguments)};n.q=[],window.FreshworksWidget=n}}()`

    reference.src = 'https://euc-widget.freshworks.com/widgets/80000006881.js'
    reference.id = 'freshdesk-widget'
    reference.async = true
    reference.defer = true

    document.body.appendChild(reference)
    document.body.appendChild(script)

    loadUserData()

    // @ts-ignore
    document.getElementById('freshdesk-widget').addEventListener('load', () => {
      loadUserData()
    })
    return () => {
      document.body.removeChild(reference)
      document.body.removeChild(script)
    }
  }, [])
  const loadUserData = () => {
    if ('FreshworksWidget' in window && user) {
      const { firstName, lastName, email } = user
      if (firstName && lastName) {
        // @ts-ignore
        window.FreshworksWidget('identify', 'ticketForm', {
          name: `${firstName} ${lastName}`,
        })
        // @ts-ignore
        // window.FreshworksWidget('disable', 'ticketForm', ['name'])
      }
      if (email && validateEmail(email)) {
        // @ts-ignore
        window.FreshworksWidget('identify', 'ticketForm', {
          email,
        })
        // @ts-ignore
        // window.FreshworksWidget('disable', 'ticketForm', ['email'])
      }
    }
    if ('FreshworksWidget' in window) {
      // @ts-ignore
      window.FreshworksWidget('prefill', 'ticketForm', {
        custom_fields: {
          cf_url: location.pathname,
        },
        tags: ['Havbruksloggen'],
      })
      // @ts-ignore
      window.FreshworksWidget('hide', 'ticketForm', ['custom_fields.cf_url'])
      // @ts-ignore
      window.FreshworksWidget('hide', 'launcher')
    }
  }
  return (
    <>
      <SidebarCtx.Provider value={{ collapsibleSidebarOpen, setCollapsibleSidebarOpen }}>
        <Flex.Row
          justifyContent={'center'}
          sx={{ backgroundColor: getThemeColor(theme, MUIThemeColors.backgroundMain), overflowX: 'hidden' }}
        >
          <Box sx={{ display: 'flex', overflowX: 'hidden', width: '100%' }} maxWidth={'1920px'}>
            <CssBaseline />
            <AppTopBar position="fixed" color="inherit">
              <TopBar />
            </AppTopBar>
            <DrawerLayout />
            <LayoutContainer
              className="layout-container"
              sx={{ paddingBottom: { xs: '0px', md: '0px' }, paddingTop: { xs: '90px' } }}
              drawerWidth={isISdebarHidden || isMedium ? 0 : !collapsibleSidebarOpen ? 80 : drawerWidth}
              viewportHeight={viewportHeight}
            >
              <Box sx={{ paddingBottom: '10px', flex: 1 }}>{children}</Box>
              <Box sx={{ marginTop: 'auto' }}>
                <Footer />
              </Box>
            </LayoutContainer>
            {isSmall && <BottomDrawer />}
          </Box>
        </Flex.Row>
      </SidebarCtx.Provider>
    </>
  )
}

export default Index

export const BottomDrawer = () => {
  const { t: translate } = useTranslation(['header', 'common'])
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { user } = useUserCtx()
  const { isIntegrationEnabled } = useFeatureFlagsCtx()

  const handleNavigate = (path: To) => {
    if (path == AppRoutesNavigation.Vessel) window.location.href = path
    navigate(path)
  }
  return (
    <Drawer
      open={true}
      anchor="bottom"
      variant="permanent"
      ModalProps={{
        keepMounted: true,
      }}
    >
      <Flex.Row justifyContent={'space-evenly'} height={'80px'}>
        {mainTopBarItems.map((nav, index) => {
          if (!hasMainNavAccess(user, nav, isIntegrationEnabled)) return null
          return (
            <NavigationContainer
              key={index}
              onClick={() => {
                handleNavigate(nav.path)
              }}
              $active={!!pathname && [nav.path, nav.main].includes(`/${pathname.split('/')[1]}`)}
            >
              <Flex.Column justifyContent={'center'} alignItems={'center'}>
                <nav.Icon height={29} width={29} />
                <Typography variant="h6" fontSize={'12px'} align="center">
                  {translate(nav.name)}
                </Typography>
              </Flex.Column>
            </NavigationContainer>
          )
        })}
      </Flex.Row>
    </Drawer>
  )
}

//styles
export const LayoutContainer = props => {
  const { pathname } = useLocation()
  const isWorkOrderPage = ['/work-orders'].some(path => pathname.endsWith(path))

  const StyledBox = useMemo(() => {
    const heightCss = isWorkOrderPage ? `min-height: ${props.viewportHeight}px;` : `height: ${props.viewportHeight}px;`

    return styled(Box)<{ drawerWidth: number; viewportHeight: number }>`
      ${padding.p16}
      padding-top: 96px;
      width: ${props => `calc(100vw - ${props.drawerWidth}px)`};
      display: flex;
      flex-direction: column;
      max-width: 100%;
      overflow-x: hidden;
      overflow-y: auto;
      ${heightCss}
      background-color: ${props => props.theme.palette.backgroundMain[props.theme.palette.mode]};
    `
  }, [isWorkOrderPage, props.viewportHeight])

  return <StyledBox {...props} />
}

export const AppTopBar = styled(AppBar)`
  z-index: ${props => props.theme.zIndex.drawer - 1};
  background-color: ${props => props.theme.palette.white[props.theme.palette.mode]};
`
const validateEmail = email => {
  return String(email)
    .toLowerCase()
    .match(
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
    )
}
